/**
 * @fileoverview Contact detail Sheet panel.
 *
 * Triggered by clicking a row in the contacts datatable. Provides four
 * tabs:
 *
 * - **Overview** — editable identity fields (display name, email, phone,
 *   identifiers) with autosave, plus the firm-link picker and merge
 *   action.
 * - **Sources** — read-only source rows grouped by agent kind, each with
 *   a deep link into the underlying summary entry.
 * - **Notes** — chronological notes feed (composer + AI summary card +
 *   newest-first list with per-note edit/delete) plus a Chat button that
 *   opens a contact-scoped Morgan chat sheet.
 * - **History** — change-history feed via the shared `ChangeHistory`
 *   component, including word-level diffs for note edits.
 *
 * The list of contacts is owned by the reconcile pipeline; this panel
 * only edits the existing record.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useAction } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  AlertCircle,
  ArrowUpRight,
  Lightbulb,
  Link as LinkIcon,
  Loader2,
  Mail,
  Merge,
  Phone,
  Plus,
  Sparkles,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import { toast } from "sonner";
import { extractErrorMessage } from "@/utils/errors";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import { Separator } from "@/ui/primitives/separator";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/ui/composed/overlays/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { useAutosave } from "@/utils/forms/use-autosave";
import { AutosaveStatus } from "@/utils/forms/autosave-status";
import { cn } from "@/utils/misc";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";
import {
  CONTACT_TYPE_META,
  FIRM_CATEGORY_META,
  SOURCE_KIND_META,
  SOURCE_KIND_ORDER,
  createEmptyAddress,
  formatAbn,
  formatAcn,
  formatAud,
  splitExposure,
  totalExposure,
  type ContactAddress,
  type ContactSourceKind,
  type ContactSourceLink,
  type ExposureDirection,
  type JobContactRow,
  type MergeSuggestion,
} from "./types";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "@/components/jobs/phases/gather/summarization/shared-panels/ChangeHistory";
import { ContactRolesEditor } from "./ContactRolesEditor";
import {
  CONTACT_FIELD_FORMATTERS,
  CONTACT_FIELD_LABELS,
} from "./historyFormatters";
import { ContactMergePicker } from "./ContactMergePicker";
import { ContactGroupSection } from "./ContactGroupSection";
import { ContactLinksSection } from "./ContactLinksSection";
import {
  ExternalAdministrationSection,
  type ExternalAdminDraft,
} from "./ExternalAdministrationSection";
import { AppointeeOfSection } from "./AppointeeOfSection";
import { AddressesEditor } from "./AddressesEditor";
import {
  EnrichmentSuggestionCard,
  type EnrichmentResult,
  type EnrichmentSuggestionShape,
} from "./EnrichmentSuggestionCard";
import { ContactActivityTab } from "./ContactActivityTab";
import { ContactChatSheet } from "./ContactChatSheet";
import { ThreadView } from "@/components/mail/ThreadView";
import { ComposeReader } from "@/components/mail/ComposeReader";
import { DraftReader } from "@/components/mail/DraftReader";
import type { UserDraftListItem } from "@/components/mail/types";
import {
  CONTACT_FIELD_PARTS,
  CONTACT_SOURCE_FIELDS,
  CONTACT_SOURCE_KIND_LABELS,
  type ContactSourceKind as CsfSourceKind,
} from "@cvx/jobs/precedents/contactFields";
import {
  type ContactDataForFields,
  resolveContactFields,
  resolveContactSourceFields,
} from "@cvx/jobs/precedents/fieldMapping";
import { deriveNameParts, composeFullName } from "@cvx/contacts/nameParts";

// ---------------------------------------------------------------------------
// Sheet wrapper
// ---------------------------------------------------------------------------

/** What the contact panel's left-hand reader pane is showing. */
export type ContactReader =
  | { kind: "thread"; threadId: Id<"mailThreads"> }
  | { kind: "draft"; draftId: Id<"mailDrafts"> }
  | { kind: "userDraft"; draftId: Id<"mailDrafts"> }
  | { kind: "compose"; to?: string[] }
  | null;

interface ContactDetailPanelProps {
  jobId: Id<"jobs">;
  contactId: Id<"jobContacts"> | null;
  open: boolean;
  onClose: () => void;
  /** Other rows on this job, used by the merge picker. */
  jobContacts: Array<JobContactRow>;
}

export function ContactDetailPanel({
  jobId,
  contactId,
  open,
  onClose,
  jobContacts,
}: ContactDetailPanelProps) {
  const requestContactDraft = useConvexMutation(
    api.mail.contactCompose.requestContactEmailDraft,
  );

  /**
   * Hands the composer's brief to Morgan. The rough notes have done their
   * job once the request is away, so the composer discards its own draft
   * rather than leaving a second copy sitting in the feed.
   */
  const handOffToMorgan = contactId
    ? (instruction: string) => {
        void requestContactDraft({ contactId, instruction })
          .then(() => {
            setReader(null);
            toast.success(
              "Morgan is drafting the email — it will appear on this contact, and in Mail → For Review, once ready.",
            );
          })
          .catch((err: unknown) =>
            toast.error(
              extractErrorMessage(err, "Could not request the draft"),
            ),
          );
      }
    : undefined;
  // Body populates this with a callback that flushes pending autosaves.
  // Fired before `onClose` so the user never loses identity-field /
  // address edits that were still mid-debounce when the sheet closed.
  const flushRef = useRef<(() => Promise<void> | void) | null>(null);

  // What the reader pane on the left is showing. Opened from the Mail &
  // Notes feed: a thread, an unsent draft, or a blank compose. Each reuses
  // the job mailbox's own component, so mail behaves identically wherever
  // it is read from.
  const [reader, setReader] = useState<ContactReader>(null);
  useEffect(() => {
    setReader(null);
  }, [contactId, open]);
  const threadId = reader?.kind === "thread" ? reader.threadId : null;

  // Mailbox address for the reader pane's reply composer.
  const { data: mailbox } = useQuery({
    ...convexQuery(api.mail.queries.getMailboxForJob, { jobId }),
    enabled: open,
  });

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setReader(null);
          void Promise.resolve(flushRef.current?.()).finally(() => {
            onClose();
          });
        }
      }}
    >
      <SheetContent
        side="right"
        className={cn(
          "flex w-full flex-row gap-0 overflow-x-hidden p-0",
          reader ? "sm:max-w-[100rem]" : "sm:max-w-3xl",
        )}
      >
        {reader && (
          <div className="flex min-w-0 flex-1 flex-col border-r border-border bg-muted/10">
            <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {reader.kind === "thread"
                  ? "Thread"
                  : reader.kind === "compose"
                    ? "New message"
                    : "Draft"}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => setReader(null)}
                aria-label="Close reader"
              >
                <X className="size-4" />
              </Button>
            </div>
            {/* Each reader manages its own scrolling, as in the mailbox. */}
            <div className="min-h-0 flex-1 overflow-hidden bg-background">
              {reader.kind === "thread" ? (
                <ThreadView
                  jobId={jobId}
                  threadId={reader.threadId}
                  mailboxEmail={mailbox?.emailAddress ?? ""}
                />
              ) : reader.kind === "draft" ? (
                // Morgan's draft, with the same approve / edit / reject /
                // revise controls as the mailbox review queue.
                <DraftReader jobId={jobId} draftId={reader.draftId} />
              ) : reader.kind === "userDraft" ? (
                <ResumeUserDraft
                  key={reader.draftId}
                  jobId={jobId}
                  draftId={reader.draftId}
                  mailboxEmail={mailbox?.emailAddress ?? ""}
                  onClose={() => setReader(null)}
                  onDraftWithMorgan={handOffToMorgan}
                />
              ) : (
                // A blank message addressed to this contact.
                <ComposeReader
                  key="new"
                  jobId={jobId}
                  mailboxEmail={mailbox?.emailAddress ?? ""}
                  prefillTo={reader.to}
                  onClose={() => setReader(null)}
                  onDraftWithMorgan={handOffToMorgan}
                  dismissAfterDraftWithMorgan
                />
              )}
            </div>
          </div>
        )}
        <div
          className={cn(
            "h-full w-full min-w-0 flex-col gap-0 overflow-x-hidden",
            // On small screens the open reader takes over the sheet; the
            // contact column returns when the reader pane is closed. Keyed
            // on the reader as a whole, not just a thread — a draft or a
            // compose has to narrow this column too, or it takes the full
            // width and squeezes the reader to nothing.
            reader ? "hidden sm:flex sm:w-[48rem] sm:flex-none" : "flex",
          )}
        >
          {contactId ? (
            <ContactDetailBody
              key={contactId}
              jobId={jobId}
              contactId={contactId}
              jobContacts={jobContacts}
              onClose={onClose}
              registerFlush={(fn) => {
                flushRef.current = fn;
              }}
              activeThreadId={threadId}
              activeReader={reader}
              onOpenThread={(id) => setReader({ kind: "thread", threadId: id })}
              onOpenReader={setReader}
            />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Resumes one of the practitioner's saved drafts in the mailbox composer.
 *
 * Its own component so the lookup only ever runs with a real draft id, and
 * so the composer — which seeds its fields once, on mount — is not mounted
 * until the record it is resuming has arrived.
 */
function ResumeUserDraft({
  jobId,
  draftId,
  mailboxEmail,
  onClose,
  onDraftWithMorgan,
}: {
  jobId: Id<"jobs">;
  draftId: Id<"mailDrafts">;
  mailboxEmail: string;
  onClose: () => void;
  onDraftWithMorgan?: (instruction: string) => void;
}) {
  const { data: draft, isPending } = useQuery(
    convexQuery(api.mail.userDrafts.getUserDraft, { draftId }),
  );

  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!draft) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
        This draft has been sent or discarded.
      </div>
    );
  }

  return (
    <ComposeReader
      jobId={jobId}
      mailboxEmail={mailboxEmail}
      existingDraft={draft as UserDraftListItem}
      onClose={onClose}
      onDraftWithMorgan={onDraftWithMorgan}
      dismissAfterDraftWithMorgan
    />
  );
}

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

type EditableFields = {
  displayName: string;
  // Person-name parts (MOR-643). Kept in the same autosave bundle; the backend
  // re-syncs Name ↔ parts and holds the invariant.
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  abn: string;
  acn: string;
};

function emptyEditable(): EditableFields {
  return {
    displayName: "",
    firstName: "",
    middleName: "",
    lastName: "",
    dateOfBirth: "",
    email: "",
    phone: "",
    abn: "",
    acn: "",
  };
}

/**
 * Build the editable name fields for a contact — stored parts when present,
 * otherwise derived from `displayName` (MOR-643) so the inputs are never blank
 * for a person whose row predates the split.
 */
function nameDraftFor(contact: {
  displayName: string;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}): { firstName: string; middleName: string; lastName: string } {
  const parts = deriveNameParts(contact);
  return {
    firstName: parts.firstName,
    middleName: parts.middleName,
    lastName: parts.lastName,
  };
}

function emptyExtAdmin(): ExternalAdminDraft {
  return { status: "", appointeeTitle: "" };
}

/** Build the external-administration text draft from a contact. */
function extAdminDraftFor(contact: {
  externalAdminStatus?: string | null;
  externalAdminAppointeeTitle?: string | null;
}): ExternalAdminDraft {
  return {
    status: contact.externalAdminStatus ?? "",
    appointeeTitle: contact.externalAdminAppointeeTitle ?? "",
  };
}

function ContactDetailBody({
  jobId,
  contactId,
  jobContacts,
  onClose,
  registerFlush,
  activeThreadId,
  activeReader,
  onOpenThread,
  onOpenReader,
}: {
  jobId: Id<"jobs">;
  contactId: Id<"jobContacts">;
  jobContacts: Array<JobContactRow>;
  onClose: () => void;
  /**
   * Wrapper passes a setter so the body can register a flush callback
   * for any in-flight autosave debounce. Called by the Sheet when the
   * user closes the panel so identity-field + address edits aren't
   * silently dropped.
   */
  registerFlush: (fn: () => Promise<void> | void) => void;
  /** Thread currently open in the wrapper's reader pane. */
  activeThreadId: Id<"mailThreads"> | null;
  /** Opens a mail thread in the wrapper's reader pane. */
  onOpenThread: (threadId: Id<"mailThreads">) => void;
  /** Whatever the wrapper's reader pane is currently showing. */
  activeReader: ContactReader;
  /** Opens a draft or a new message in the wrapper's reader pane. */
  onOpenReader: (reader: ContactReader) => void;
}) {
  const { data: contact, isLoading } = useQuery({
    ...convexQuery(api.contacts.queries.getJobContact, { contactId }),
  });

  const updateFields = useConvexMutation(
    api.contacts.mutations.updateJobContactFields,
  );
  const linkToFirm = useConvexMutation(
    api.contacts.mutations.linkJobContactToFirm,
  );
  const unlinkFromFirm = useConvexMutation(
    api.contacts.mutations.unlinkJobContactFromFirm,
  );
  const mergeContacts = useConvexMutation(
    api.contacts.mutations.mergeJobContacts,
  );
  const unmergeContact = useConvexMutation(
    api.contacts.mutations.unmergeJobContact,
  );
  const dismissSuggestion = useConvexMutation(
    api.contacts.mutations.dismissMergeSuggestion,
  );
  const deleteContact = useConvexMutation(
    api.contacts.mutations.deleteJobContact,
  );
  const enrichJobContact = useAction(api.contacts.enrichment.enrichJobContact);
  const applyEnrichment = useConvexMutation(
    api.contacts.mutations.applyContactEnrichment,
  );

  // Enrichment agent state — null when idle, "loading" while the action
  // is in flight, or holding the latest result for the suggestion card.
  const [enrichmentState, setEnrichmentState] = useState<
    | { stage: "idle" }
    | { stage: "loading"; mode: "sources" | "web_search" }
    | { stage: "result"; result: EnrichmentResult; isApplying: boolean }
  >({ stage: "idle" });

  // Local edit drafts for fields. Synced from server data when the contact
  // changes, but kept independent during typing so the user can clear a
  // field without losing it to a re-render.
  const [draft, setDraft] = useState<EditableFields>(emptyEditable);
  const draftRef = useRef<EditableFields>(emptyEditable());
  draftRef.current = draft;

  // Addresses draft — kept independent from the identity-field autosave so
  // editing a row doesn't churn the identity diff.
  const [addressesDraft, setAddressesDraft] = useState<Array<ContactAddress>>(
    [],
  );
  const addressesDraftRef = useRef<Array<ContactAddress>>([]);
  addressesDraftRef.current = addressesDraft;

  // External administration overlay draft — kept independent from the identity
  // autosave (like addresses) so editing it doesn't churn the identity diff.
  // `appointees` is free-text with one name per line; split into an array on
  // save.
  const [extAdminDraft, setExtAdminDraft] =
    useState<ExternalAdminDraft>(emptyExtAdmin);
  const extAdminDraftRef = useRef<ExternalAdminDraft>(emptyExtAdmin());
  extAdminDraftRef.current = extAdminDraft;

  // True while the user is editing any of the four interdependent name inputs
  // (Name / First / Middle / Last). The backend re-syncs the others on save
  // (MOR-643); we mirror that back into the draft when the server values
  // change — but only while no name field is focused, so we never clobber an
  // in-progress edit.
  const nameFocusRef = useRef(false);
  const lastSyncedNameSigRef = useRef<string | null>(null);

  // Contact-scoped chat sidesheet. Toggled from the Notes header.
  const [chatOpen, setChatOpen] = useState(false);

  // Controlled tabs so the exposure cards can deep-link into Sources.
  // `exposureFilter` highlights the source rows that make up a header total.
  const [activeTab, setActiveTab] = useState<ContactPanelTab>("overview");
  const [exposureFilter, setExposureFilter] = useState<ExposureFilter | null>(
    null,
  );

  const goToExposureSources = (filter: ExposureFilter) => {
    setExposureFilter(filter);
    setActiveTab("sources");
  };

  // Identity of the calling user — used to decide which note rows are
  // editable and to render avatars in the chat sidesheet.
  const { data: currentUser } = useQuery({
    ...convexQuery(api.users.queries.getCurrentUser, {}),
  });
  const currentUserId = currentUser?._id;

  // Resolve the practitioner names + avatars referenced by the change-history
  // feed. Batched into a single Convex subscription so a 30-entry history with
  // 2 distinct authors only hits the users table twice.
  const historyUserIds = useMemo(() => {
    const ids = new Set<Id<"users">>();
    for (const entry of contact?.changeHistory ?? []) {
      if (entry.userId) ids.add(entry.userId);
    }
    return Array.from(ids);
  }, [contact?.changeHistory]);
  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, {
      ids: historyUserIds,
    }),
    enabled: historyUserIds.length > 0,
  });
  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) {
      map.set(u._id, u);
    }
    return map;
  }, [historyUsers]);

  // Mail tab badge — unique thread count for this contact's correspondence
  // (same query the Mail tab timeline uses). Zero when there's no email.
  const contactEmail = (contact?.email ?? "").trim();
  const { data: mailMessages } = useQuery({
    ...convexQuery(api.mail.commsQueries.listMessagesForContact, {
      jobId,
      emailAddress: contactEmail,
    }),
    enabled: contactEmail.length > 0,
  });
  const mailThreadCount = useMemo(() => {
    if (!mailMessages || mailMessages.length === 0) return 0;
    const threadIds = new Set<string>();
    for (const message of mailMessages) {
      if (message.threadId) threadIds.add(message.threadId);
    }
    return threadIds.size;
  }, [mailMessages]);

  // Tracks the most recent "merge into this" action so we can render a
  // success banner with an inline Undo button. Cleared on a timer or
  // immediately on undo.
  const [recentMerge, setRecentMerge] = useState<{
    loserId: Id<"jobContacts">;
    loserName: string;
    sourcesAdded: number;
  } | null>(null);
  const recentMergeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (recentMergeTimer.current) clearTimeout(recentMergeTimer.current);
    };
  }, []);

  // Track which contact `_id` we last hydrated so we don't overwrite
  // pending edits while the user is typing.
  const hydratedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!contact) return;
    if (hydratedForRef.current === contact._id) return;
    hydratedForRef.current = contact._id;
    setDraft({
      displayName: contact.displayName,
      ...nameDraftFor(contact),
      dateOfBirth: contact.dateOfBirth ?? "",
      email: contact.email ?? "",
      phone: contact.phone ?? "",
      abn: contact.abn ?? "",
      acn: contact.acn ?? "",
    });
    setAddressesDraft(contact.addresses);
    setExtAdminDraft(extAdminDraftFor(contact));
    setEnrichmentState({ stage: "idle" });
  }, [contact]);

  // ---------------------------------------------------------------------
  // Autosave wiring (overlay fields + addresses). Note bodies live in
  // the chronological feed and have their own per-row save flow.
  // ---------------------------------------------------------------------
  const fieldsAutosave = useAutosave<EditableFields>({
    getValue: () => draftRef.current,
    save: async (value) => {
      await updateFields({
        contactId,
        patch: {
          displayName: value.displayName,
          firstName: value.firstName,
          middleName: value.middleName,
          lastName: value.lastName,
          dateOfBirth: value.dateOfBirth,
          email: value.email,
          phone: value.phone,
          abn: value.abn,
          acn: value.acn,
        },
      });
    },
  });

  const addressesAutosave = useAutosave<Array<ContactAddress>>({
    getValue: () => addressesDraftRef.current,
    save: async (value) => {
      await updateFields({
        contactId,
        patch: {
          addresses: value.map((a) => ({
            line1: a.line1,
            line2: a.line2,
            suburb: a.suburb,
            state: a.state,
            postcode: a.postcode,
            country: a.country,
            sourceLabel: a.sourceLabel,
          })),
        },
      });
    },
  });

  const extAdminAutosave = useAutosave<ExternalAdminDraft>({
    getValue: () => extAdminDraftRef.current,
    save: async (value) => {
      await updateFields({
        contactId,
        patch: {
          externalAdminStatus: value.status,
          externalAdminAppointeeTitle: value.appointeeTitle,
        },
      });
    },
  });

  // Re-baseline all autosavers whenever the server doc identity changes
  // so the diff comparison starts clean.
  useEffect(() => {
    if (!contact) return;
    fieldsAutosave.resetBaseline({
      displayName: contact.displayName,
      ...nameDraftFor(contact),
      dateOfBirth: contact.dateOfBirth ?? "",
      email: contact.email ?? "",
      phone: contact.phone ?? "",
      abn: contact.abn ?? "",
      acn: contact.acn ?? "",
    });
    addressesAutosave.resetBaseline(contact.addresses);
    extAdminAutosave.resetBaseline(extAdminDraftFor(contact));
    // We intentionally only depend on the server identity to avoid loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?._id]);

  // Mirror server-side name re-syncs into the draft (MOR-643). When the
  // backend re-splits Name into parts (or recomposes Name from parts), the
  // sibling inputs would otherwise show stale values until the panel reopens —
  // the per-`_id` hydration above won't re-run. Watch the server name tuple
  // and refresh the name draft fields, skipping while a name field is focused.
  useEffect(() => {
    if (!contact) return;
    const sig = [
      contact.displayName,
      contact.firstName ?? "",
      contact.middleName ?? "",
      contact.lastName ?? "",
    ].join(" ");
    if (lastSyncedNameSigRef.current === null) {
      lastSyncedNameSigRef.current = sig;
      return;
    }
    if (lastSyncedNameSigRef.current === sig) return;
    if (nameFocusRef.current) return;
    lastSyncedNameSigRef.current = sig;
    const next = {
      ...draftRef.current,
      displayName: contact.displayName,
      ...nameDraftFor(contact),
    };
    setDraft(next);
    fieldsAutosave.resetBaseline(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contact?.displayName,
    contact?.firstName,
    contact?.middleName,
    contact?.lastName,
  ]);

  // Hand the wrapper a callback that flushes any in-flight autosaves.
  // Fired by the Sheet's `onOpenChange(false)` before `onClose` runs.
  useEffect(() => {
    registerFlush(async () => {
      await Promise.allSettled([
        Promise.resolve(fieldsAutosave.flush()),
        Promise.resolve(addressesAutosave.flush()),
        Promise.resolve(extAdminAutosave.flush()),
      ]);
    });
    // We deliberately re-register on every render because the autosave
    // hooks return new closures — the cost is negligible (just a ref
    // assignment) and it keeps the captured `flush` references fresh.
  });

  if (isLoading || !contact) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading contact…
      </div>
    );
  }

  const TypeIcon = CONTACT_TYPE_META[contact.contactType].icon;
  const sourcesByKind = new Map<ContactSourceKind, Array<ContactSourceLink>>();
  for (const s of contact.sourceLinks) {
    const list = sourcesByKind.get(s.kind) ?? [];
    list.push(s);
    sourcesByKind.set(s.kind, list);
  }
  const exposure = totalExposure(contact);
  const exposureSplit = splitExposure(contact);
  const isMerged = contact.status === "merged";
  const isArchived = contact.status === "archived";

  // ---------------------------------------------------------------------
  // Action handlers
  // ---------------------------------------------------------------------
  /**
   * Merge from the explicit "Merge with…" picker. The picker semantics
   * are "pick the surviving contact" — so the user-chosen target is the
   * winner and the contact whose panel is open becomes the loser. The
   * panel closes because the row the user was viewing has just been
   * archived as merged.
   */
  /**
   * Delete the open contact (MOR-930). The backend keeps a hidden tombstone
   * so the reconcile pipeline doesn't re-create the contact from its agent
   * sources. The panel closes because the row no longer exists in the list.
   */
  const handleDelete = () => {
    deleteContact({ contactId })
      .then(() => {
        toast.success(`Deleted "${contact?.displayName ?? "contact"}"`);
        onClose();
      })
      .catch((e: Error) =>
        toast.error(e.message || "Could not delete the contact."),
      );
  };

  const handleMergeFromPicker = (winnerId: Id<"jobContacts">) => {
    mergeContacts({ winnerId, loserId: contactId })
      .then(() => {
        toast.success("Contacts merged");
        onClose();
      })
      .catch((e: Error) =>
        toast.error(e.message || "Could not merge contacts."),
      );
  };

  /**
   * Merge from an inline "this looks like a duplicate" suggestion. The
   * mental model is "absorb the duplicate into the contact I'm viewing"
   * — so the open contact stays as the winner and the suggestion is the
   * loser. The panel stays open and reactively re-renders with the
   * combined source links / firm link / etc, and surfaces a "just
   * merged X — Undo" banner so the user gets clear visual confirmation.
   */
  const handleMergeSuggestion = (suggestion: MergeSuggestion) => {
    const beforeSourceCount = contact?.sourceLinks.length ?? 0;
    mergeContacts({ winnerId: contactId, loserId: suggestion.contactId })
      .then(() => {
        toast.success(`Absorbed "${suggestion.displayName}"`);
        // Banner pulls the post-merge source count from the next render.
        setRecentMerge({
          loserId: suggestion.contactId,
          loserName: suggestion.displayName,
          sourcesAdded: Math.max(0, suggestion.sourceKindCount),
        });
        if (recentMergeTimer.current) clearTimeout(recentMergeTimer.current);
        recentMergeTimer.current = setTimeout(() => {
          setRecentMerge(null);
        }, 8000);
        // Mark the variable as used so the linter doesn't complain when
        // we want to keep this around for a future "X new sources" diff.
        void beforeSourceCount;
      })
      .catch((e: Error) =>
        toast.error(e.message || "Could not merge contacts."),
      );
  };

  /**
   * Reverse the most recent suggestion-driven merge so the user can
   * recover instantly if the absorption was wrong.
   */
  const handleUndoRecentMerge = () => {
    if (!recentMerge) return;
    const target = recentMerge;
    unmergeContact({ loserId: target.loserId })
      .then(() => {
        toast.success(`Restored "${target.loserName}"`);
        setRecentMerge(null);
        if (recentMergeTimer.current) clearTimeout(recentMergeTimer.current);
      })
      .catch((e: Error) =>
        toast.error(e.message || "Could not undo the merge."),
      );
  };

  const handleUnmerge = () => {
    unmergeContact({ loserId: contactId })
      .then(() => toast.success("Merge reversed"))
      .catch((e: Error) =>
        toast.error(e.message || "Could not undo the merge."),
      );
  };

  const handleLink = (firmContactId: Id<"firmContacts">) => {
    linkToFirm({ contactId, firmContactId })
      .then(() => toast.success("Linked to firm contact"))
      .catch((e: Error) =>
        toast.error(e.message || "Could not link to firm contact."),
      );
  };

  const handleUnlink = () => {
    unlinkFromFirm({ contactId })
      .then(() => toast.success("Firm link removed"))
      .catch((e: Error) =>
        toast.error(e.message || "Could not remove firm link."),
      );
  };

  const handleDismissSuggestion = (otherContactId: Id<"jobContacts">) => {
    dismissSuggestion({ contactId, otherContactId })
      .then(() => toast.success("Suggestion dismissed"))
      .catch((e: Error) =>
        toast.error(e.message || "Could not dismiss the suggestion."),
      );
  };

  // ---------------------------------------------------------------------
  // Enrichment agent
  // ---------------------------------------------------------------------
  const runEnrichment = (mode: "sources" | "web_search") => {
    setEnrichmentState({ stage: "loading", mode });
    enrichJobContact({ contactId, mode })
      .then((result) => {
        setEnrichmentState({
          stage: "result",
          result: result as EnrichmentResult,
          isApplying: false,
        });
      })
      .catch((e: Error) => {
        setEnrichmentState({ stage: "idle" });
        toast.error(e.message || "Enrichment agent failed.");
      });
  };

  const handleApplyEnrichment = (selection: EnrichmentSuggestionShape) => {
    if (enrichmentState.stage !== "result") return;
    const { result } = enrichmentState;
    setEnrichmentState((s) =>
      s.stage === "result" ? { ...s, isApplying: true } : s,
    );
    applyEnrichment({
      contactId,
      source: result.source,
      apply: {
        email: selection.email,
        phone: selection.phone,
        dateOfBirth: selection.dateOfBirth,
        addresses:
          selection.addresses.length > 0
            ? selection.addresses.map((a) => ({
                line1: a.line1,
                line2: a.line2,
                suburb: a.suburb,
                state: a.state,
                postcode: a.postcode,
                country: a.country,
                sourceLabel: a.sourceLabel,
              }))
            : undefined,
      },
      citations: result.citations,
      reasoning: result.reasoning,
      model: result.model,
    })
      .then(() => {
        const where =
          result.source === "web_search" ? "web search" : "source documents";
        toast.success(`Enriched contact (${where} approved)`);

        // Sync local drafts so the UI reflects the enrichment immediately
        // and the autosave doesn't overwrite the backend with stale data.
        const { email: appliedEmail, phone: appliedPhone } = selection;
        if (appliedEmail !== undefined) {
          setDraft((prev) => ({ ...prev, email: appliedEmail }));
        }
        if (appliedPhone !== undefined) {
          setDraft((prev) => ({ ...prev, phone: appliedPhone }));
        }
        if (selection.addresses.length > 0) {
          setAddressesDraft((prev) => [...prev, ...selection.addresses]);
        }

        setEnrichmentState({ stage: "idle" });
      })
      .catch((e: Error) => {
        setEnrichmentState((s) =>
          s.stage === "result" ? { ...s, isApplying: false } : s,
        );
        toast.error(e.message || "Could not apply the enrichment.");
      });
  };

  return (
    <>
      <SheetHeader className="space-y-2 border-b border-border px-6 pb-4 pt-6">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-lg bg-muted p-2">
            <TypeIcon className="h-5 w-5 text-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <SheetTitle className="text-lg leading-tight">
              {contact.displayName}
            </SheetTitle>
            <SheetDescription className="flex items-center gap-1.5 pt-1.5 text-xs">
              <span className="font-medium text-foreground/70">Type</span>
              <Badge
                color={
                  contact.contactType === "person"
                    ? "blue"
                    : contact.contactType === "authority"
                      ? "amber"
                      : "gray"
                }
                variant="soft"
                size="sm"
              >
                {CONTACT_TYPE_META[contact.contactType].label}
              </Badge>
            </SheetDescription>
          </div>
          {!isMerged && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-0.5 h-8 w-8 shrink-0 p-0 text-primary/40 hover:bg-destructive/10 hover:text-destructive"
                  title="Delete contact"
                  aria-label="Delete contact"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this contact?</AlertDialogTitle>
                  <AlertDialogDescription>
                    &ldquo;{contact.displayName}&rdquo; will be removed from
                    this job, along with its notes and history. It will not be
                    re-created by future contact refreshes. This action cannot
                    be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {/* Roles — editable with manual override (MOR-676) */}
        <ContactRolesEditor contact={contact} disabled={isMerged} />

        {/* Identifier badges + status */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {contact.acn && contact.contactType !== "person" && (
            <Badge
              variant="outline"
              size="sm"
              color="gray"
              className="font-mono"
            >
              ACN {formatAcn(contact.acn)}
            </Badge>
          )}
          {contact.abn && (
            <Badge
              variant="outline"
              size="sm"
              color="gray"
              className="font-mono"
            >
              ABN {formatAbn(contact.abn)}
            </Badge>
          )}
          {contact.firmContact && (
            <Badge
              color={
                FIRM_CATEGORY_META[contact.firmContact.category].badgeColor
              }
              variant="soft"
              size="sm"
              className="gap-1"
            >
              <LinkIcon className="h-3 w-3" />
              {contact.firmContact.displayName}
            </Badge>
          )}
          {isMerged && (
            <Badge color="violet" variant="soft" size="sm">
              Merged
            </Badge>
          )}
          {isArchived && (
            <Badge color="gray" variant="soft" size="sm">
              Archived
            </Badge>
          )}
        </div>

        {/* Exposure — amounts roll up from sourceLinks; "See sources"
            jumps to the Sources tab and highlights contributing rows. */}
        {exposure > 0 && (
          <div className="grid grid-cols-2 gap-3 pt-3">
            {exposureSplit.creditor > 0 && (
              <ExposureCard
                label="Owed to contact (est.)"
                amount={exposureSplit.creditor}
                onSeeSources={() => goToExposureSources("creditor")}
              />
            )}
            {exposureSplit.debtor > 0 && (
              <ExposureCard
                label="Owed by contact (est.)"
                amount={exposureSplit.debtor}
                amountClassName="text-emerald-600 dark:text-emerald-500"
                onSeeSources={() => goToExposureSources("debtor")}
              />
            )}
            {exposureSplit.unclassified > 0 &&
              exposureSplit.creditor === 0 &&
              exposureSplit.debtor === 0 && (
                <ExposureCard
                  label="Estimated Exposure"
                  amount={exposureSplit.unclassified}
                  onSeeSources={() => goToExposureSources("unclassified")}
                />
              )}
          </div>
        )}
      </SheetHeader>

      {isMerged && (
        <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/30 px-6 py-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-2">
            <AlertCircle className="h-3.5 w-3.5" />
            This contact has been merged into another. Edits are disabled.
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleUnmerge}
            className="gap-2"
          >
            <Undo2 className="h-3.5 w-3.5" />
            Undo merge
          </Button>
        </div>
      )}

      {recentMerge && !isMerged && (
        <div className="flex items-center justify-between gap-2 border-b border-emerald-200 bg-emerald-50/70 px-6 py-2.5 text-xs text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-100">
          <span className="flex min-w-0 items-center gap-2">
            <Merge className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              <span className="font-medium">
                &ldquo;{recentMerge.loserName}&rdquo;
              </span>{" "}
              was absorbed into this contact. Its source rows now live under{" "}
              {contact.displayName}.
            </span>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleUndoRecentMerge}
            className="shrink-0 gap-2 border-emerald-300 bg-white/60 text-emerald-900 hover:bg-white dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-100"
          >
            <Undo2 className="h-3.5 w-3.5" />
            Undo
          </Button>
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          const next = value as ContactPanelTab;
          setActiveTab(next);
          if (next !== "sources") setExposureFilter(null);
        }}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="px-6 pt-3">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Details</TabsTrigger>
            <TabsTrigger value="comms">
              Mail &amp; Notes
              <span className="ml-1.5 rounded-sm bg-muted px-1 text-[10px] tabular-nums text-muted-foreground">
                {mailThreadCount + contact.notesCount}
              </span>
            </TabsTrigger>
            <TabsTrigger value="sources">
              Sources
              <span className="ml-1.5 rounded-sm bg-muted px-1 text-[10px] tabular-nums text-muted-foreground">
                {contact.sourceLinks.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="fields">Fields</TabsTrigger>
            <TabsTrigger value="history">
              History
              <span className="ml-1.5 rounded-sm bg-muted px-1 text-[10px] tabular-nums text-muted-foreground">
                {contact.changeHistory.length}
              </span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* The viewport's injected `display: table` wrapper sizes to the
            content's max-content width and blows out horizontally past the
            sheet — force it back to a normal block that fills the panel. */}
        <ScrollArea className="min-h-0 flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:w-full">
          {/* ----------------------- OVERVIEW ----------------------- */}
          <TabsContent value="overview" className="mt-0 px-6 py-5">
            <div className="flex flex-col gap-5">
              {/* Identity edit form */}
              <section className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                    Details
                  </h3>
                  <div className="flex items-center gap-2">
                    {!isMerged && enrichmentState.stage === "idle" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1.5 text-xs"
                        onClick={() => runEnrichment("sources")}
                      >
                        <Sparkles className="h-3 w-3" />
                        Enrich with AI
                      </Button>
                    )}
                    <AutosaveStatus
                      status={fieldsAutosave.status}
                      lastSavedAt={fieldsAutosave.lastSavedAt}
                      onRetry={fieldsAutosave.retry}
                    />
                  </div>
                </div>

                {enrichmentState.stage === "loading" && (
                  <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {enrichmentState.mode === "web_search"
                      ? "Searching the web for contact details…"
                      : "Reading source documents…"}
                  </div>
                )}

                {enrichmentState.stage === "result" && (
                  <EnrichmentSuggestionCard
                    result={enrichmentState.result}
                    jobId={jobId}
                    isEscalating={false}
                    isApplying={enrichmentState.isApplying}
                    onApply={handleApplyEnrichment}
                    onEscalateToWebSearch={() => runEnrichment("web_search")}
                    onDismiss={() => setEnrichmentState({ stage: "idle" })}
                  />
                )}
                <div className="grid grid-cols-1 gap-3">
                  <Field
                    label="Name"
                    hint={
                      contact.contactType === "person"
                        ? "First + Middle + Last — stays in sync with the parts below"
                        : undefined
                    }
                  >
                    <Input
                      value={draft.displayName}
                      disabled={isMerged}
                      onFocus={() => {
                        nameFocusRef.current = true;
                      }}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, displayName: e.target.value }))
                      }
                      onBlur={() => {
                        nameFocusRef.current = false;
                        fieldsAutosave.flush();
                      }}
                    />
                  </Field>
                  {/* Person-only: first / middle / last name (MOR-643,
                      MOR-976). The backend keeps these in sync with Name
                      (Name = First + Middle + Last — moving a token into
                      Middle keeps it in the Name); the composed full name is
                      shown as a live preview while the parts are being edited
                      and the Name input still holds the pre-save value. */}
                  {contact.contactType === "person" && (
                    <>
                      <div className="grid grid-cols-3 gap-3">
                        <Field label="First name">
                          <Input
                            value={draft.firstName}
                            disabled={isMerged}
                            onFocus={() => {
                              nameFocusRef.current = true;
                            }}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                firstName: e.target.value,
                              }))
                            }
                            onBlur={() => {
                              nameFocusRef.current = false;
                              fieldsAutosave.flush();
                            }}
                          />
                        </Field>
                        <Field label="Middle name">
                          <Input
                            value={draft.middleName}
                            disabled={isMerged}
                            placeholder="Optional"
                            onFocus={() => {
                              nameFocusRef.current = true;
                            }}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                middleName: e.target.value,
                              }))
                            }
                            onBlur={() => {
                              nameFocusRef.current = false;
                              fieldsAutosave.flush();
                            }}
                          />
                        </Field>
                        <Field label="Last name">
                          <Input
                            value={draft.lastName}
                            disabled={isMerged}
                            onFocus={() => {
                              nameFocusRef.current = true;
                            }}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                lastName: e.target.value,
                              }))
                            }
                            onBlur={() => {
                              nameFocusRef.current = false;
                              fieldsAutosave.flush();
                            }}
                          />
                        </Field>
                      </div>
                      {composeFullName(
                        draft.firstName,
                        draft.middleName,
                        draft.lastName,
                      ) && (
                        <p className="-mt-1 text-xs text-muted-foreground">
                          Full name:{" "}
                          <span className="font-medium text-foreground">
                            {composeFullName(
                              draft.firstName,
                              draft.middleName,
                              draft.lastName,
                            )}
                          </span>
                        </p>
                      )}
                    </>
                  )}
                  {/* Person-only: date of birth (MOR-562) — enterable on the
                      job contact and mapped into documents via
                      `contact_job_date_of_birth`. */}
                  {contact.contactType === "person" && (
                    <Field label="Date of birth">
                      <DatePicker
                        value={draft.dateOfBirth || undefined}
                        disabled={isMerged}
                        placeholder="Select date of birth"
                        onChange={(next) => {
                          const value = next ?? "";
                          setDraft((d) => ({ ...d, dateOfBirth: value }));
                          // DatePicker commits on selection (no blur). The
                          // autosave reads `draftRef.current` at flush time,
                          // which only refreshes on render — so update it
                          // synchronously before flushing to avoid saving the
                          // previous date.
                          draftRef.current = {
                            ...draftRef.current,
                            dateOfBirth: value,
                          };
                          void fieldsAutosave.flush();
                        }}
                      />
                    </Field>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <Field
                      label="Email"
                      icon={<Mail className="h-3 w-3" />}
                      hint={
                        contact.emailFromFirm && contact.firmContact?.email
                          ? `from firm: ${contact.firmContact.email}`
                          : undefined
                      }
                    >
                      <Input
                        type="email"
                        value={draft.email}
                        placeholder={
                          contact.firmContact?.email ?? "name@example.com"
                        }
                        disabled={isMerged}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, email: e.target.value }))
                        }
                        onBlur={() => fieldsAutosave.flush()}
                      />
                    </Field>
                    <Field
                      label="Phone"
                      icon={<Phone className="h-3 w-3" />}
                      hint={
                        contact.phoneFromFirm && contact.firmContact?.phone
                          ? `from firm: ${contact.firmContact.phone}`
                          : undefined
                      }
                    >
                      <Input
                        value={draft.phone}
                        placeholder={contact.firmContact?.phone ?? "+61 …"}
                        disabled={isMerged}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, phone: e.target.value }))
                        }
                        onBlur={() => fieldsAutosave.flush()}
                      />
                    </Field>
                  </div>
                  <div
                    className={`grid gap-3 ${contact.contactType === "person" ? "grid-cols-1" : "grid-cols-2"}`}
                  >
                    <Field label="ABN">
                      <Input
                        value={draft.abn}
                        placeholder="11 digits"
                        disabled={isMerged}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            abn: e.target.value.replace(/\s/g, ""),
                          }))
                        }
                        onBlur={() => fieldsAutosave.flush()}
                        className="font-mono"
                      />
                    </Field>
                    {contact.contactType !== "person" && (
                      <Field label="ACN">
                        <Input
                          value={draft.acn}
                          placeholder="9 digits"
                          disabled={isMerged}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              acn: e.target.value.replace(/\s/g, ""),
                            }))
                          }
                          onBlur={() => fieldsAutosave.flush()}
                          className="font-mono"
                        />
                      </Field>
                    )}
                  </div>
                </div>

                {/* External administration overlay — company contacts only.
                    Lives directly under the identifiers: a company's EXAD
                    status belongs with its identity, not at the foot of the
                    panel. A compact toggle row that expands inline when the
                    company is in external administration. */}
                {contact.contactType !== "person" && (
                  <ExternalAdministrationSection
                    contact={contact}
                    jobContacts={jobContacts}
                    draft={extAdminDraft}
                    // Update the ref synchronously so an `onCommit` fired in
                    // the same tick (the toggle) flushes the new value rather
                    // than the pre-render snapshot.
                    onChange={(next) => {
                      extAdminDraftRef.current = next;
                      setExtAdminDraft(next);
                    }}
                    onCommit={() => extAdminAutosave.flush()}
                    suggestion={contact.externalAdminSuggestion}
                    // Confirming a suggestion writes status/title on the
                    // server; mirror them into the draft AND the autosave
                    // baseline so the section reflects them without queuing
                    // a redundant save (hydration only runs per `_id`).
                    onSuggestionApplied={(next) => {
                      extAdminDraftRef.current = next;
                      setExtAdminDraft(next);
                      extAdminAutosave.resetBaseline(next);
                    }}
                    disabled={isMerged}
                    autosaveStatus={extAdminAutosave.status}
                    lastSavedAt={extAdminAutosave.lastSavedAt}
                    onRetry={extAdminAutosave.retry}
                  />
                )}
              </section>

              <Separator />

              <section className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                    Addresses
                  </h4>
                  <div className="flex items-center gap-2">
                    {addressesDraft.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1.5 text-xs"
                        disabled={isMerged}
                        type="button"
                        onClick={() => {
                          const next = [
                            ...addressesDraftRef.current,
                            createEmptyAddress(),
                          ];
                          addressesDraftRef.current = next;
                          setAddressesDraft(next);
                          void addressesAutosave.flush();
                        }}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add address
                      </Button>
                    )}
                    <AutosaveStatus
                      status={addressesAutosave.status}
                      lastSavedAt={addressesAutosave.lastSavedAt}
                      onRetry={addressesAutosave.retry}
                    />
                  </div>
                </div>
                <AddressesEditor
                  addresses={addressesDraft}
                  disabled={isMerged}
                  showTrailingAdd={false}
                  onChange={(next) => setAddressesDraft(next)}
                  onCommit={() => addressesAutosave.flush()}
                />
              </section>

              <Separator />

              <ContactLinksSection
                contact={contact}
                jobContacts={jobContacts}
                disabled={isMerged}
                onLinkFirm={handleLink}
                onUnlinkFirm={handleUnlink}
              />

              {/* Appointee-side view of external administration links */}
              {contact.administeredCompanies &&
                contact.administeredCompanies.length > 0 && (
                  <>
                    <Separator />
                    <AppointeeOfSection
                      companies={contact.administeredCompanies}
                    />
                  </>
                )}

              <Separator />

              <section className="flex flex-col gap-4">
                <h3 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
                  Grouping and merging
                </h3>

                <ContactGroupSection
                  contact={contact}
                  jobContacts={jobContacts}
                  disabled={isMerged}
                />

                <div className="flex flex-col gap-2">
                  <h4 className="font-sans text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Merge
                  </h4>
                  {isMerged ? (
                    <p className="text-xs text-muted-foreground">
                      Already merged into another contact. Use &ldquo;Undo
                      merge&rdquo; above to reverse.
                    </p>
                  ) : contact.mergeSuggestions.length > 0 ? (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs text-muted-foreground">
                          We spotted{" "}
                          {contact.mergeSuggestions.length === 1
                            ? "one possible duplicate"
                            : `${contact.mergeSuggestions.length} possible duplicates`}{" "}
                          on this job. Review each suggestion below.
                        </p>
                        <ContactMergePicker
                          current={contact}
                          candidates={jobContacts}
                          onPick={handleMergeFromPicker}
                        />
                      </div>
                      <ul className="flex flex-col gap-2">
                        {contact.mergeSuggestions.map((s) => (
                          <MergeSuggestionRow
                            key={s.contactId}
                            suggestion={s}
                            onMerge={() => handleMergeSuggestion(s)}
                            onDismiss={() =>
                              handleDismissSuggestion(s.contactId)
                            }
                          />
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-muted/10 p-3">
                      <p className="text-xs text-muted-foreground">
                        No potential duplicates. Use &ldquo;Merge&rdquo; to
                        combine records. Details from the merged record will be
                        pulled into this record and merges can be reversed at
                        any time.
                      </p>
                      <ContactMergePicker
                        current={contact}
                        candidates={jobContacts}
                        onPick={handleMergeFromPicker}
                      />
                    </div>
                  )}
                </div>
              </section>
            </div>
          </TabsContent>

          {/* ------------------------ SOURCES ------------------------ */}
          <TabsContent value="sources" className="mt-0 px-6 py-5">
            <SourcesTab
              jobId={jobId}
              sourcesByKind={sourcesByKind}
              exposureFilter={exposureFilter}
              emptyMessage={
                contact.sourceLinks.length === 0
                  ? "This contact has no active source rows. It was preserved because it carries notes or a merge history."
                  : undefined
              }
            />
          </TabsContent>

          {/* -------------------------- FIELDS ------------------------- */}
          <TabsContent value="fields" className="mt-0 px-6 py-5">
            <FieldsTab jobId={jobId} contactId={contactId} contact={contact} />
          </TabsContent>

          {/* --------------------- NOTES & MAIL ----------------------- */}
          <TabsContent value="comms" className="mt-0 px-6 py-5">
            <ContactActivityTab
              jobId={jobId}
              contactId={contactId}
              emailAddress={contact.email ?? ""}
              readOnly={isMerged}
              currentUserId={currentUserId}
              notesSummary={contact.notesSummary}
              notesCount={contact.notesCount}
              onOpenChat={() => setChatOpen(true)}
              onOpenThread={onOpenThread}
              activeThreadId={activeThreadId}
              activeReader={activeReader}
              onOpenReader={onOpenReader}
            />
          </TabsContent>

          {/* ------------------------ HISTORY ------------------------ */}
          <TabsContent value="history" className="mt-0 px-6 py-5">
            {contact.changeHistory.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-muted/10 p-6 text-center text-sm text-muted-foreground">
                No history yet. Changes you make to this contact will appear
                here.
              </div>
            ) : (
              <ChangeHistory
                history={contact.changeHistory}
                usersById={historyUsersById}
                formatters={CONTACT_FIELD_FORMATTERS}
                fieldLabels={CONTACT_FIELD_LABELS}
                title="Change history"
                defaultOpen
              />
            )}
          </TabsContent>
        </ScrollArea>
        <AiDisclaimer className="border-t border-border px-6 py-2" />
      </Tabs>

      <ContactChatSheet
        open={chatOpen}
        onOpenChange={setChatOpen}
        contactId={contactId}
        contactName={contact.displayName}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Exposure header card
// ---------------------------------------------------------------------------

type ContactPanelTab = "overview" | "comms" | "sources" | "fields" | "history";

/** Header exposure bucket used to highlight contributing source rows. */
type ExposureFilter = ExposureDirection | "unclassified";

/**
 * Header tile for a rolled-up exposure total. "See sources" switches the
 * panel to the Sources tab and highlights the rows that make up this total.
 */
function ExposureCard({
  label,
  amount,
  amountClassName,
  onSeeSources,
}: {
  label: string;
  amount: number;
  amountClassName?: string;
  onSeeSources: () => void;
}) {
  return (
    <div className="min-w-0 rounded-xl border bg-muted/30 px-4 py-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-0.5 text-2xl font-semibold tabular-nums text-foreground",
          amountClassName,
        )}
      >
        {formatAud(amount)}
      </div>
      <button
        type="button"
        onClick={onSeeSources}
        className="mt-1 text-[11px] font-medium text-primary hover:underline"
      >
        See sources
      </button>
    </div>
  );
}

/** Whether a source row contributes to a given exposure header bucket. */
function sourceMatchesExposureFilter(
  source: ContactSourceLink,
  filter: ExposureDirection | "unclassified",
): boolean {
  if (typeof source.amount !== "number" || source.amount === 0) return false;
  if (filter === "unclassified") return source.exposureDirection == null;
  return source.exposureDirection === filter;
}

// ---------------------------------------------------------------------------
// Sources tab
// ---------------------------------------------------------------------------

function SourcesTab({
  jobId,
  sourcesByKind,
  exposureFilter,
  emptyMessage,
}: {
  jobId: Id<"jobs">;
  sourcesByKind: Map<ContactSourceKind, Array<ContactSourceLink>>;
  /** When set (via an exposure-card "See sources"), highlight matching rows. */
  exposureFilter?: ExposureDirection | "unclassified" | null;
  emptyMessage?: string;
}) {
  const orderedKinds = useMemo(
    () => SOURCE_KIND_ORDER.filter((k) => sourcesByKind.has(k)),
    [sourcesByKind],
  );

  if (emptyMessage) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/10 p-6 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  const filterLabel =
    exposureFilter === "creditor"
      ? "Owed to contact"
      : exposureFilter === "debtor"
        ? "Owed by contact"
        : exposureFilter === "unclassified"
          ? "Estimated exposure"
          : null;

  return (
    <section className="flex flex-col gap-4">
      {filterLabel && (
        <p className="text-xs text-muted-foreground">
          Highlighting sources for{" "}
          <span className="font-medium text-foreground">{filterLabel}</span>.
        </p>
      )}
      {orderedKinds.map((kind) => {
        const meta = SOURCE_KIND_META[kind];
        const list = sourcesByKind.get(kind) ?? [];
        return (
          <div
            key={kind}
            className="flex flex-col gap-2 rounded-lg border border-border bg-background"
          >
            <div className="flex items-center justify-between gap-3 px-3 pt-3">
              <div className="flex min-w-0 items-center gap-2">
                <meta.icon className="h-4 w-4 text-muted-foreground" />
                <span className="truncate text-sm font-medium text-foreground">
                  {meta.label}
                </span>
                <Badge
                  color={meta.badgeColor}
                  variant="soft"
                  size="sm"
                  className="ml-1"
                >
                  {list.length}
                </Badge>
              </div>
              <Link
                to="/dashboard/jobs/$jobId/summaries/$agent"
                params={{ jobId, agent: meta.summarySlug }}
                className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-primary hover:underline"
              >
                Open
                <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
            <Separator />
            <ul className="flex flex-col">
              {list.map((source, i) => (
                <SourceRow
                  key={`${source.sourceId}-${i}`}
                  source={source}
                  isLast={i === list.length - 1}
                  highlighted={
                    !!exposureFilter &&
                    sourceMatchesExposureFilter(source, exposureFilter)
                  }
                  dimmed={
                    !!exposureFilter &&
                    !sourceMatchesExposureFilter(source, exposureFilter)
                  }
                />
              ))}
            </ul>
          </div>
        );
      })}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Fields tab
// ---------------------------------------------------------------------------

/**
 * Shows all field values that would be available when generating precedent
 * documents for this contact — basic identity fields plus source-data fields
 * derived from linked agent entries.
 */
function FieldsTab({
  jobId,
  contactId,
  contact,
}: {
  jobId: Id<"jobs">;
  contactId: Id<"jobContacts">;
  contact: {
    displayName: string;
    contactType?: string | null;
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
    dateOfBirth?: string | null;
    email?: string | null;
    phone?: string | null;
    abn?: string | null;
    acn?: string | null;
    effectiveEmail?: string | null;
    effectivePhone?: string | null;
    addresses: Array<{
      line1?: string;
      line2?: string;
      suburb?: string;
      state?: string;
      postcode?: string;
      country?: string;
      sourceLabel: string;
    }>;
    sourceLinks: Array<ContactSourceLink>;
    externalAdminStatus?: string | null;
    externalAdminAppointeeTitle?: string | null;
    externalAdminAppointee?: ContactDataForFields["externalAdminAppointee"];
    primaryContactPerson?: ContactDataForFields["primaryContactPerson"];
  };
}) {
  const { data: sourceRows, isLoading } = useQuery({
    ...convexQuery(api.contacts.queries.getJobContactSourceRows, {
      jobId,
      jobContactId: contactId,
    }),
  });

  // Basic contact fields
  const basicFields = useMemo(
    () =>
      resolveContactFields(
        {
          displayName: contact.displayName,
          contactType: contact.contactType,
          firstName: contact.firstName,
          middleName: contact.middleName,
          lastName: contact.lastName,
          dateOfBirth: contact.dateOfBirth,
          email: contact.effectiveEmail ?? contact.email,
          phone: contact.effectivePhone ?? contact.phone,
          abn: contact.abn,
          acn: contact.acn,
          addresses: contact.addresses,
          externalAdminStatus: contact.externalAdminStatus,
          externalAdminAppointeeTitle: contact.externalAdminAppointeeTitle,
          externalAdminAppointee: contact.externalAdminAppointee,
          primaryContactPerson: contact.primaryContactPerson,
        },
        "job",
      ),
    [contact],
  );

  // Source-data fields grouped by source kind
  const sourceFieldGroups = useMemo(() => {
    if (!sourceRows) return [];
    const groups: Array<{
      kind: CsfSourceKind;
      label: string;
      entries: Array<{
        linkLabel: string;
        fields: Record<string, string>;
      }>;
    }> = [];

    for (const kind of Object.keys(sourceRows) as CsfSourceKind[]) {
      const rows = (
        sourceRows as Record<
          string,
          Array<{
            link: { sourceId: string; roleLabel?: string; detail?: string };
            row: unknown;
          }>
        >
      )[kind];
      if (!rows || rows.length === 0) continue;
      const entries = rows.map((r) => ({
        linkLabel:
          r.link.roleLabel || r.link.detail || CONTACT_SOURCE_KIND_LABELS[kind],
        // Pass the link id so joint-registration rows resolve the
        // secured-party fields for THIS contact's entity (MOR-708).
        fields: resolveContactSourceFields(kind, r.row, "job", r.link.sourceId),
      }));
      // Only include groups that actually resolved at least one field
      const hasFields = entries.some((e) => Object.keys(e.fields).length > 0);
      if (!hasFields) continue;
      groups.push({
        kind,
        label: CONTACT_SOURCE_KIND_LABELS[kind],
        entries,
      });
    }
    return groups;
  }, [sourceRows]);

  const basicEntries = Object.entries(basicFields);
  const hasBasicFields = basicEntries.length > 0;
  const hasSourceFields = sourceFieldGroups.length > 0;

  if (!hasBasicFields && !hasSourceFields && !isLoading) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/10 p-6 text-center text-sm text-muted-foreground">
        No fields available for this contact. Fields are derived from identity
        data and agent source entries.
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-5">
      {/* Basic contact fields */}
      {hasBasicFields && (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-background">
          <div className="flex items-center gap-2 px-3 pt-3">
            <span className="text-sm font-medium text-foreground">
              Contact Details
            </span>
          </div>
          <Separator />
          <div className="grid grid-cols-1 gap-px bg-border">
            {basicEntries.map(([key, value]) => {
              const partKey = key.replace(/^contact_job_/, "");
              const partMeta = CONTACT_FIELD_PARTS.find(
                (p) => p.part === partKey,
              );
              return (
                <div
                  key={key}
                  className="flex items-start justify-between gap-3 bg-background px-3 py-2"
                >
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {partMeta?.label ?? partKey}
                  </span>
                  <span className="whitespace-pre-line text-right text-sm text-foreground">
                    {value}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Source-data fields */}
      {isLoading && (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading source fields…
        </div>
      )}

      {sourceFieldGroups.map((group) => (
        <div
          key={group.kind}
          className="flex flex-col gap-2 rounded-lg border border-border bg-background"
        >
          <div className="flex items-center gap-2 px-3 pt-3">
            <span className="text-sm font-medium text-foreground">
              {group.label}
            </span>
            <Badge color="gray" variant="soft" size="sm">
              {group.entries.length}
            </Badge>
          </div>
          <Separator />
          <div className="flex flex-col divide-y divide-border">
            {group.entries.map((entry, entryIdx) => {
              const fieldEntries = Object.entries(entry.fields);
              if (fieldEntries.length === 0) return null;
              const fieldMetas = CONTACT_SOURCE_FIELDS[group.kind];
              return (
                <div key={entryIdx} className="flex flex-col gap-px px-3 py-2">
                  {group.entries.length > 1 && (
                    <span className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
                      {entry.linkLabel}
                    </span>
                  )}
                  <div className="grid grid-cols-1 gap-1">
                    {fieldEntries.map(([key, value]) => {
                      // Extract the part from the key to find metadata
                      const parts = key.split("_");
                      // Key format: contact_job_<kind>_<part> — we need the part after the kind
                      const kindParts = group.kind.split("_");
                      const partSegment = parts
                        .slice(2 + kindParts.length)
                        .join("_");
                      const meta = fieldMetas.find(
                        (m) => m.part === partSegment,
                      );
                      return (
                        <div
                          key={key}
                          className="flex items-start justify-between gap-3 py-0.5"
                        >
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {meta?.label ?? partSegment}
                          </span>
                          <span className="text-right text-sm text-foreground">
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}

function SourceRow({
  source,
  isLast,
  highlighted = false,
  dimmed = false,
}: {
  source: ContactSourceLink;
  isLast: boolean;
  /** Emphasise rows that make up the active exposure header total. */
  highlighted?: boolean;
  /** Soften non-matching rows while an exposure filter is active. */
  dimmed?: boolean;
}) {
  return (
    <li
      className={cn(
        "flex items-start justify-between gap-3 px-3 py-2.5 transition-colors",
        !isLast && "border-b border-border",
        highlighted && "bg-primary/5",
        dimmed && "opacity-40",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm text-foreground">{source.roleLabel}</div>
        {source.detail && (
          <div className="line-clamp-2 text-xs text-muted-foreground">
            {source.detail}
          </div>
        )}
      </div>
      {source.amount != null && source.amount !== 0 && (
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          {source.exposureDirection && (
            <span
              className={cn(
                "text-[10px] uppercase tracking-wider",
                source.exposureDirection === "debtor"
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-muted-foreground",
              )}
            >
              {source.exposureDirection === "debtor" ? "Owed by" : "Owed to"}
            </span>
          )}
          <span
            className={cn(
              "text-sm font-semibold tabular-nums",
              source.exposureDirection === "debtor"
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-foreground",
            )}
          >
            {formatAud(source.amount)}
          </span>
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Field helper
// ---------------------------------------------------------------------------

function Field({
  label,
  icon,
  hint,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground/80">
        {icon}
        {label}
      </Label>
      {children}
      {hint && (
        <span className="text-[10px] text-muted-foreground/70">{hint}</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Merge suggestion row
// ---------------------------------------------------------------------------

const MATCH_LABEL: Record<MergeSuggestion["matchedOn"][number], string> = {
  name: "name",
  name_fuzzy: "similar name",
  email: "email",
  phone: "phone",
  acn: "ACN",
  abn: "ABN",
  bankrupt_role: "both tagged Bankrupt",
  date_of_birth: "date of birth",
};

function MergeSuggestionRow({
  suggestion,
  onMerge,
  onDismiss,
}: {
  suggestion: MergeSuggestion;
  onMerge: () => void;
  onDismiss: () => void;
}) {
  const TypeIcon = CONTACT_TYPE_META[suggestion.contactType].icon;
  return (
    <li className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-900/40 dark:bg-amber-950/20">
      <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <TypeIcon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="truncate text-sm font-medium text-foreground">
            {suggestion.displayName}
          </span>
          {suggestion.acn && (
            <span className="font-mono text-[10px] tabular-nums text-muted-foreground/80">
              ACN {formatAcn(suggestion.acn)}
            </span>
          )}
          {suggestion.abn && (
            <span className="font-mono text-[10px] tabular-nums text-muted-foreground/80">
              ABN {formatAbn(suggestion.abn)}
            </span>
          )}
        </div>
        <div className="mt-1 text-[11px] text-muted-foreground">
          Matched on{" "}
          <span className="font-medium text-foreground/80">
            {suggestion.matchedOn.map((m) => MATCH_LABEL[m]).join(", ")}
          </span>
          {suggestion.sourceKindCount > 0 && (
            <>
              {" · "}
              {suggestion.sourceKindCount} agent
              {suggestion.sourceKindCount === 1 ? "" : "s"}
            </>
          )}
          {suggestion.email && <> · {suggestion.email}</>}
        </div>
      </div>
      <div className="flex shrink-0 gap-1.5">
        <Button
          size="sm"
          variant="default"
          onClick={onMerge}
          className="h-7 gap-1.5"
          title="Absorb this duplicate into the contact you're viewing"
        >
          <Merge className="h-3.5 w-3.5" />
          Merge into this
        </Button>
        <Button size="sm" variant="ghost" onClick={onDismiss} className="h-7">
          Dismiss
        </Button>
      </div>
    </li>
  );
}
